import { AdminApiError } from "./adminApi";

export type LoadResult<T> = readonly [T | null, string | null];

export function extract<T>(r: PromiseSettledResult<T>): LoadResult<T> {
  if (r.status === "fulfilled") return [r.value, null];
  const reason = r.reason as unknown;
  const msg =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : "unknown error";
  return [null, msg];
}

// Map an admin API error to the route it should redirect to.
// An absent/expired session (401 with body.error === "no_session") means
// "log in again" → /login. Any other 401 means "authenticated but not an
// admin" → /access-denied. Non-401 errors are handled inline by the caller.
export function adminErrorRedirect(
  err: unknown,
): "/login" | "/access-denied" | null {
  if (err instanceof AdminApiError && err.status === 401) {
    const body = err.body as { error?: string } | undefined;
    return body?.error === "no_session" ? "/login" : "/access-denied";
  }
  return null;
}

// Same discrimination applied across a batch of settled results. First
// rejected entry that maps to a redirect wins.
export function authRedirectFrom(
  results: ReadonlyArray<PromiseSettledResult<unknown>>,
): "/login" | "/access-denied" | null {
  for (const r of results) {
    if (r.status === "rejected") {
      const to = adminErrorRedirect(r.reason);
      if (to) return to;
    }
  }
  return null;
}
